import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, CaretDown, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Combobox de categorías. Permite seleccionar una categoría/subcategoría existente
 * o escribir un nombre nuevo y crearla en el momento (raíz o subcategoría).
 */
export const CategoryCombobox = ({ value, onChange, categories, onCategoriesChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [createParentId, setCreateParentId] = useState('none');

  const selected = categories.find((c) => c.id === value);
  const selectedLabel = selected
    ? (selected.parent_id
        ? `${categories.find((p) => p.id === selected.parent_id)?.name || '?'} › ${selected.name}`
        : selected.name)
    : 'Sin categoría';

  const parents = categories.filter((c) => !c.parent_id);
  const orphanSubs = categories.filter(
    (c) => c.parent_id && !categories.some((p) => p.id === c.parent_id)
  );

  const trimmed = search.trim();
  const exactMatch = categories.find(
    (c) => c.name.toLowerCase() === trimmed.toLowerCase()
  );

  const handleCreate = async () => {
    if (!trimmed) return;
    try {
      const payload = {
        name: trimmed,
        parent_id: createParentId === 'none' ? null : createParentId
      };
      const { data } = await axios.post(`${API}/categories`, payload, { withCredentials: true });
      const tipo = createParentId === 'none' ? 'Categoría' : 'Subcategoría';
      toast.success(`${tipo} "${trimmed}" creada`);
      if (onCategoriesChange) await onCategoriesChange();
      onChange(data.id);
      setSearch('');
      setCreateParentId('none');
      setOpen(false);
    } catch (error) {
      toast.error('Error al crear categoría');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid="product-category-combobox"
        >
          <span className={selected ? '' : 'text-muted-foreground'}>{selectedLabel}</span>
          <CaretDown size={16} className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar o escribir nueva..."
            value={search}
            onValueChange={setSearch}
            data-testid="category-combobox-input"
          />
          <CommandList>
            <CommandEmpty className="p-0">
              {trimmed ? (
                <div className="p-2 space-y-2">
                  <p className="text-xs text-muted-foreground px-1">Crear "{trimmed}" como:</p>
                  <Select value={createParentId} onValueChange={setCreateParentId}>
                    <SelectTrigger className="h-8 text-sm" data-testid="category-create-parent-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Categoría raíz</SelectItem>
                      {parents.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          Subcategoría de: {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    onClick={handleCreate}
                    size="sm"
                    className="w-full"
                    data-testid="category-combobox-create"
                  >
                    <Plus size={14} className="mr-1" />
                    Crear
                  </Button>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Sin categorías. Escribe para crear una.
                </p>
              )}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value=""
                onSelect={() => {
                  onChange('none');
                  setOpen(false);
                }}
              >
                <Check
                  size={16}
                  className={`mr-2 ${value === 'none' ? 'opacity-100' : 'opacity-0'}`}
                />
                <span className="text-muted-foreground">Sin categoría</span>
              </CommandItem>
              {parents
                .filter((p) =>
                  !trimmed ||
                  p.name.toLowerCase().includes(trimmed.toLowerCase()) ||
                  categories.some(
                    (s) =>
                      s.parent_id === p.id &&
                      s.name.toLowerCase().includes(trimmed.toLowerCase())
                  )
                )
                .map((parent) => {
                  const subs = categories.filter((s) => s.parent_id === parent.id);
                  const filteredSubs = subs.filter(
                    (s) => !trimmed || s.name.toLowerCase().includes(trimmed.toLowerCase())
                  );
                  const parentMatches =
                    !trimmed || parent.name.toLowerCase().includes(trimmed.toLowerCase());
                  return [
                    parentMatches && (
                      <CommandItem
                        key={parent.id}
                        value={parent.name}
                        onSelect={() => {
                          onChange(parent.id);
                          setOpen(false);
                        }}
                      >
                        <Check
                          size={16}
                          className={`mr-2 ${value === parent.id ? 'opacity-100' : 'opacity-0'}`}
                        />
                        <span className="font-medium">{parent.name}</span>
                      </CommandItem>
                    ),
                    ...filteredSubs.map((sub) => (
                      <CommandItem
                        key={sub.id}
                        value={`${parent.name} ${sub.name}`}
                        onSelect={() => {
                          onChange(sub.id);
                          setOpen(false);
                        }}
                      >
                        <Check
                          size={16}
                          className={`mr-2 ${value === sub.id ? 'opacity-100' : 'opacity-0'}`}
                        />
                        <span className="text-muted-foreground mr-2">↳</span>
                        <span>{sub.name}</span>
                      </CommandItem>
                    ))
                  ];
                })}
              {orphanSubs
                .filter((s) => !trimmed || s.name.toLowerCase().includes(trimmed.toLowerCase()))
                .map((sub) => (
                  <CommandItem
                    key={sub.id}
                    value={sub.name}
                    onSelect={() => {
                      onChange(sub.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      size={16}
                      className={`mr-2 ${value === sub.id ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <span>{sub.name}</span>
                  </CommandItem>
                ))}
            </CommandGroup>
            {trimmed && !exactMatch && (
              <div className="border-t p-2 space-y-2 bg-slate-50">
                <p className="text-xs text-muted-foreground px-1">Crear "{trimmed}" como:</p>
                <Select value={createParentId} onValueChange={setCreateParentId}>
                  <SelectTrigger className="h-8 text-sm" data-testid="category-create-parent-select-bottom">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Categoría raíz</SelectItem>
                    {parents.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        Subcategoría de: {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleCreate}
                  size="sm"
                  className="w-full"
                  data-testid="category-combobox-create-bottom"
                >
                  <Plus size={14} className="mr-1" />
                  Crear
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
